import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell, { storedSession } from '../components/AppShell';
import AddCandidateDialog from '../components/candidates/AddCandidateDialog';
import CandidateRow from '../components/candidates/CandidateRow';
import { canUseCandidates, Evidence, fmtDateTime, StagePill } from '../components/candidates/common';
import { GradeTile, RiskBadge } from '../components/candidates/MatchPanel';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, Skeleton, ToastProvider, useToast } from '../components/ui/Kit';

// /jobs/:id/candidates — Find candidates (CandSearch, mockup L; bundle 4b). Criteria come from the intake + approved JD;
// every must-have / scored criterion and the location radius is a toggle with a live count — toggling recomputes from
// the stored per-criterion scores (no AI). The list shows as soon as scores are cached; "Scoring 3 of 12…" updates live.
// Candidates who miss a must-have sit in a collapsed section with what they miss. Scores never reject anyone.

const POLL_MS = 2500;
const RESULT_ICON = { pass: '✓', fail: '✗', unknown: '?' };
const RESULT_TONE = { pass: 'bg-[#DCFCE7] text-[#166534]', fail: 'bg-[#FEE2E2] text-[#991B1B]', unknown: 'bg-[#F1F5F9] text-[#475569]' };
const SOURCE_PILL = { live: 'bg-[#E2E8F0] text-[#334155]', not_connected: 'bg-[#F1F5F9] text-[#475569]', coming_soon: 'bg-[#F1F5F9] text-[#475569]' };
const segCls = (on) => `inline-flex items-center gap-1.5 min-h-[36px] px-3.5 rounded-[9px] text-[13px] font-semibold whitespace-nowrap ${on ? 'bg-white text-[#0F172A] shadow-[0_1px_3px_rgba(15,23,42,.1)]' : 'text-[#475569]'}`;

function Seg({ label, children }) {
  return <nav className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl max-w-full overflow-x-auto" aria-label={label}>{children}</nav>;
}

// "+2 if off" — how many more people meet the must-haves without it ("−1 if on" for one that's off)
const delta = (d, on) => (on ? `+${Math.max(0, d)} if off` : `−${Math.max(0, -d)} if on`);

function Criteria({ data, toggle, setStep }) {
  const n = data.counts;
  return (
    <section className={card + ' p-4 sm:p-5 flex flex-col gap-3 rounded-2xl'} aria-label="Search criteria" data-testid="criteria">
      <div className="flex flex-wrap gap-2.5 items-center">
        <span className="text-xs font-extrabold text-[#3730A3] uppercase tracking-[.05em] w-[110px]">Must-haves</span>
        {data.must_haves.map((m) => (
          <button key={m.key} type="button" aria-pressed={m.on} onClick={() => toggle(m.key)} data-testid={`must-${m.key}`}
            title={`${n.must_haves[m.key]} would meet the must-haves if you turn this ${m.on ? 'off' : 'on'}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-[5px] rounded-lg text-xs font-semibold ${m.on ? 'bg-[#EEF2FF] text-[#3730A3]' : 'bg-[#F8FAFC] text-[#94A3B8] line-through'}`}>
            <span title={m.original && m.original !== m.label ? `Intake: ${m.original}` : undefined}>{m.label}</span><span className="font-normal text-[11px] opacity-80">{delta(n.must_haves[m.key] - n.meeting, m.on)}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2.5 items-center">
        <span className="text-xs font-extrabold text-[#475569] uppercase tracking-[.05em] w-[110px]">Scored</span>
        {data.scored.filter((s) => s.weight > 0).map((s) => (
          <button key={s.key} type="button" aria-pressed={s.on} onClick={() => toggle(s.key)} data-testid={`scored-${s.key}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-[5px] rounded-lg text-xs font-semibold ${s.on ? 'bg-[#F1F5F9] text-[#334155]' : 'bg-[#F8FAFC] text-[#94A3B8] line-through'}`}>
            {s.label} <b className="font-extrabold">{s.weight}%</b>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2.5 items-center">
        <span className="text-xs font-extrabold text-[#475569] uppercase tracking-[.05em] w-[110px]">Location</span>
        <Seg label="Location radius">
          {data.location_steps.map((s) => (
            <button key={s.key} type="button" aria-pressed={data.toggles.location_step === s.key} onClick={() => setStep(s.key)}
              className={segCls(data.toggles.location_step === s.key)} data-testid={`step-${s.key}`}>
              {s.label}<span className="text-[11px] font-normal opacity-80">{n.location_steps[s.key]}</span></button>
          ))}
        </Seg>
      </div>
      <p className="text-[11px] text-[#94A3B8] sm:pl-[120px]" data-testid="geonames">Location data © <a href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer" className="underline">GeoNames</a> (CC BY 4.0)</p>
    </section>
  );
}

function ResultCard({ r, onAdd, adding, jobId, open, onOpen, onClose }) {
  const navigate = useNavigate();
  const profile = `/candidates/${r.candidate_id}?job=${jobId}`;
  const high = r.risk === 'high';
  return (
    <CandidateRow id={r.candidate_id} open={open} onOpen={onOpen} onClose={onClose} testid="result" name={r.full_name} link={profile}
      jobs={r.jobs || []} hidden={r.hidden_jobs || 0} lead={<GradeTile grade={r.grade} total={r.total} />}
      subtitle={[r.current_title, r.current_company, r.where, r.years != null ? `${r.years} yrs` : null, r.team ? `leads ${r.team}` : null].filter(Boolean).join(' · ')}>
      <div className="flex gap-2 items-center flex-wrap">
        <RiskBadge risk={r.risk} />
        {r.verify && <span className="px-2 py-[3px] rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]">Verify</span>}
        {r.application && r.application.owner && <span className="px-2 py-[3px] rounded-full text-[11px] font-semibold bg-[#EDE9FE] text-[#5B21B6]">Owned by {r.application.owner}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="must-chips">
        {r.must_haves.filter((m) => m.on).map((m) => (
          <span key={m.key} className={`text-[11.5px] font-semibold px-2 py-[3px] rounded-md ${RESULT_TONE[m.result]}`}
            title={[m.original && m.original !== m.label ? `Intake: ${m.original}` : null, m.overridden ? 'Set by a recruiter' : null].filter(Boolean).join(' · ') || undefined}>
            {RESULT_ICON[m.result]} {m.detail || m.label}</span>
        ))}
      </div>
      {r.why && <p className="text-[13px] text-[#334155] mt-2"><Evidence text={r.why} quote={false} max={180} /></p>}
      <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1 text-[12.5px] mt-2">
        <div><dt className="text-[#64748B]">Work auth</dt><dd className="font-bold">{r.facts.work_authorization}</dd></div>
        <div><dt className="text-[#64748B]">Expected</dt><dd className="font-bold">{r.facts.expected || '—'}</dd></div>
        <div><dt className="text-[#64748B]">Notice</dt><dd className="font-bold">{r.facts.notice || '—'}</dd></div>
        <div><dt className="text-[#64748B]">Source</dt><dd>{(r.facts.source || '').replace(/_/g, ' ')}</dd></div>
        <div><dt className="text-[#64748B]">Last activity</dt><dd>{fmtDateTime(r.last_activity_at)}</dd></div>
      </dl>
      {high && (
        <div className="flex gap-2.5 items-start px-3 py-2.5 mt-2 rounded-[10px] bg-[#FEF2F2] border border-[#FECACA] text-[#7F1D1D] text-[13px]" role="alert">
          <span aria-hidden="true">▲</span>
          <div><b>Strong match, but verify before contacting.</b> {(r.risk_reasons || []).join(' · ')}{' '}
            <button type="button" className="font-bold underline" onClick={() => navigate(`${profile}#authenticity`)}>See checklist →</button></div>
        </div>
      )}
      <div className="flex gap-2 mt-2.5">
        {r.application
          ? <span className="inline-flex items-center gap-2"><StagePill stage={r.application.stage} status={r.application.status} />
            <button type="button" className="inline-flex items-center justify-center gap-1.5 min-h-[36px] px-3.5 rounded-[10px] text-[13px] font-semibold bg-[#DCFCE7] text-[#166534]"
              onClick={() => navigate(`${profile}#app-${r.application.id}`)} data-testid="added">✓ Added · View</button></span>
          : <button type="button" className={btnPrimary + ' justify-center min-h-[36px] rounded-[10px]'} disabled={adding} onClick={() => onAdd(r)} data-testid="add-to-job">{adding ? 'Adding…' : 'Add to job'}</button>}
      </div>
    </CandidateRow>
  );
}

function Body() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [toggles, setToggles] = useState(null);            // {off, location_step}; null = the job's saved / default
  const [page, setPage] = useState(1);
  const [grade, setGrade] = useState('all');
  const [hideHigh, setHideHigh] = useState(false);
  const [includeOn, setIncludeOn] = useState(false);       // 4f: people already on the job are left out unless this is on
  const [adding, setAdding] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    clearTimeout(timer.current);
    return api(`/api/candidates/jobs/${id}/match-search`, { method: 'POST', body: { ...(toggles || {}), page, ...(includeOn ? { include_on_job: true } : {}) } })
      .then((d) => {
        setData(d); setError(null);
        if (d.progress.pending > 0) timer.current = setTimeout(() => load(), POLL_MS);
      }).catch((e) => setError(e));
  }, [id, toggles, page, includeOn]);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);
  useEffect(() => { api('/api/candidates/sources').then((s) => setSources(s.sources)).catch(() => setSources([])); }, []);
  const current = data ? data.toggles : { off: [], location_step: '50' };
  const toggle = (key) => {
    const off = new Set(current.off);
    if (off.has(key)) off.delete(key); else off.add(key);
    setToggles({ ...current, off: [...off] });
  };
  const setStep = (s) => setToggles({ ...current, location_step: s });
  const saveCriteria = async () => {
    setSaving(true);
    try { await api(`/api/candidates/jobs/${id}/match-criteria`, { method: 'PUT', body: current }); toast.show('Saved as the job’s criteria'); }
    catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };
  const addToJob = async (r) => {
    setAdding(r.candidate_id);
    try { await api(`/api/candidates/${r.candidate_id}/applications`, { method: 'POST', body: { requirement_id: Number(id) } }); toast.show(`${r.full_name} added at Sourced`); await load(); }
    catch (e) { toast.show(e.message, 'error'); }
    setAdding(null);
  };
  if (error) {
    return <div className={card + ' p-6'}><InlineError>{error.status === 404 ? 'This job doesn’t exist or isn’t yours.' : `${error.message}${error.reference ? ` (reference ${error.reference})` : ''}`}</InlineError>
      <button className={btnSecondary + ' mt-3'} onClick={() => navigate('/jobs')}>← Jobs</button></div>;
  }
  if (!data) return <div className={card + ' p-6 space-y-3'}><Skeleton className="h-6 w-1/3" /><Skeleton /><Skeleton /></div>;
  const j = data.job;
  const shown = data.results.filter((r) => (grade === 'all' || r.grade === grade) && !(hideHigh && r.risk === 'high'));
  const localOn = !current.off.includes('local_first');
  const p = data.progress;
  const exp = j.experience && (j.experience.min || j.experience.max) ? `${j.experience.min || '?'}–${j.experience.max || '?'} yrs` : null;
  return (
    <div className="flex flex-col gap-4" data-testid="job-candidates">
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex-[1_1_360px] min-w-0">
          <button className="text-[13px] font-semibold text-[#0066CC]" onClick={() => navigate('/jobs')}>← My jobs</button>
          <h1 className="mt-1 text-[26px] font-extrabold text-[#0F172A]">Find candidates · {j.title}</h1>
          <p className="text-[13px] text-[#64748B] mt-1">{[j.req_id, j.location, j.work_mode, exp].filter(Boolean).join(' · ')} · criteria built from Jessica’s intake call and the approved JD</p>
        </div>
        <button className={btnSecondary} onClick={() => setAddOpen(true)}>Add candidate</button>
        <button className={btnPrimary} onClick={saveCriteria} disabled={saving} data-testid="save-criteria">Save as the job’s criteria</button>
      </div>
      <Criteria data={data} toggle={toggle} setStep={setStep} />
      <div className="flex gap-3 items-center flex-wrap">
        <Seg label="Sources">
          {sources.map((s) => (
            <span key={s.key} className={segCls(s.key === 'internal')} title={s.detail}>{s.label}
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${SOURCE_PILL[s.state] || SOURCE_PILL.not_connected}`}>
                {s.state === 'live' ? s.count : s.state === 'coming_soon' ? 'Coming soon' : 'Not connected'}</span></span>
          ))}
        </Seg>
        <div className="flex-1" />
        <Seg label="Grade filter">
          {['A', 'B', 'C'].map((g) => <button key={g} type="button" aria-pressed={grade === g} className={segCls(grade === g)} onClick={() => setGrade(g)} data-testid={`grade-${g}`}>{g} · {data.grades[g]}</button>)}
          <button type="button" aria-pressed={grade === 'all'} className={segCls(grade === 'all')} onClick={() => setGrade('all')}>All</button>
        </Seg>
        <button type="button" className={btnSecondary} aria-pressed={localOn} onClick={() => toggle('local_first')}>Local first {localOn ? '✓' : ''}</button>
        <button type="button" className={btnSecondary} aria-pressed={hideHigh} onClick={() => setHideHigh(!hideHigh)} data-testid="hide-high">Hide high-risk{hideHigh ? ' ✓' : ''}</button>
        <button type="button" className={btnSecondary} aria-pressed={includeOn} onClick={() => { setIncludeOn(!includeOn); setPage(1); }} data-testid="include-on-job">
          Include people already on this job{includeOn ? ' ✓' : ''}</button>
      </div>
      {data.already_on_job > 0 && !includeOn && (
        <p className="text-xs text-[#64748B]" data-testid="already-on-job">{data.already_on_job} already on this job —{' '}
          <button type="button" className="text-[#0066CC] font-semibold hover:underline" onClick={() => navigate(`/jobs/${id}`)}>view</button></p>
      )}
      <p className="text-xs text-[#64748B]" aria-live="polite" data-testid="progress">
        {p.pending > 0 ? `Scoring ${p.scored} of ${p.total}…` : `${p.scored} scored`}{p.failed ? ` · ${p.failed} couldn’t be scored yet (retrying)` : ''} · {data.source === 'postgres' ? 'Internal database' : 'Search index'}</p>
      {!shown.length ? (
        <div className={card + ' p-2 rounded-2xl'}><EmptyState title={p.pending > 0 ? 'Scoring candidates…' : 'No one meets the must-haves yet'}
          hint={p.pending > 0 ? 'Results appear here as they are scored.' : 'Turn off a must-have or widen the location to see more people.'} /></div>
      ) : <ul className={card + ' rounded-2xl overflow-hidden'} data-testid="results">{shown.map((r) => <ResultCard key={r.candidate_id} r={r} onAdd={addToJob}
        adding={adding === r.candidate_id} jobId={id} open={openId === r.candidate_id} onOpen={setOpenId}
        onClose={(cid) => setOpenId((o) => (o === cid ? null : o))} />)}</ul>}
      {data.not_meeting.length > 0 && (
        <details className={card + ' rounded-2xl'} data-testid="not-meeting">
          <summary className="px-5 py-3.5 cursor-pointer text-sm font-bold text-[#334155]">{data.not_meeting.length} don’t meet must-haves</summary>
          <ul className="divide-y divide-[#EEF1F6] px-5 pb-3">
            {data.not_meeting.map((r) => (
              <li key={r.candidate_id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                <button type="button" className="font-semibold text-[#0F172A] hover:text-[#0066CC] text-left sm:w-48 truncate" onClick={() => navigate(`/candidates/${r.candidate_id}?job=${id}`)}>{r.full_name}</button>
                <span className="text-[#64748B] sm:w-56 truncate">{[r.current_title, r.where].filter(Boolean).join(' · ')}</span>
                <span className="flex flex-wrap gap-1.5">{r.failed.map((f) => <span key={f} className="text-[11.5px] font-semibold px-2 py-[3px] rounded-md bg-[#FEE2E2] text-[#991B1B]">✗ {f}</span>)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {data.has_more && <button type="button" className={btnSecondary + ' self-center'} onClick={() => setPage(page + 1)} data-testid="show-more">Show more (score the next 50)</button>}
      <p className="text-xs text-[#64748B]" data-testid="fairness">Grade A = every must-have met. {data.fairness}</p>
      <AddCandidateDialog open={addOpen} onClose={() => setAddOpen(false)} requirementId={Number(id)} jobTitle={j.title} onAdded={load} />
    </div>
  );
}

export default function JobCandidatesPage() {
  const navigate = useNavigate();
  const allowed = canUseCandidates(storedSession().user);
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
    else if (!allowed) navigate('/jobs');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!allowed) return null;
  return <ToastProvider><AppShell active="jobs"><Body /></AppShell></ToastProvider>;
}
